import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useCompaniesUseCases } from "../useCases/UseCasesContext";
import type { Company } from "@domain/CRM/company";
import type { User } from "@domain/Auth";
import type { CompanyContextValue } from "./CompanyContext.types";

export function useCompanyContextViewModel(user: User | undefined): CompanyContextValue {
	const companiesUseCase = useCompaniesUseCases();
	const [companies, setCompanies] = useState<Company[]>([]);
	const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const setSelectedCompany = useCallback(
		(company: Company | null) => {
			setSelectedCompanyState(company);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (company) {
						next.set("company", company.id);
					} else {
						next.delete("company");
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const replaceCompany = useCallback((company: Company) => {
		// Keep `prev` when nothing changes (company not listed, or the same object handed back by a
		// failed re-read) so every context consumer isn't re-rendered for nothing.
		setCompanies((prev) => {
			const index = prev.findIndex((c) => c.id === company.id);
			if (index === -1 || prev[index] === company) return prev;
			return prev.map((c, i) => (i === index ? company : c));
		});
		setSelectedCompanyState((prev) => (prev?.id === company.id ? company : prev));
	}, []);

	useEffect(() => {
		if (!user?.id) {
			return;
		}

		let cancelled = false;

		async function fetchData() {
			setIsLoading(true);
			try {
				const urlCompanyId = searchParams.get("company");
				const resolution = await companiesUseCase.resolveInitialCompany(user!, urlCompanyId);
				if (cancelled) return;

				setCompanies(resolution.companies);
				setSelectedCompanyState(resolution.selected);
				if (resolution.shouldWriteToUrl && resolution.selected) {
					const id = resolution.selected.id;
					setSearchParams(
						(prev) => {
							const next = new URLSearchParams(prev);
							next.set("company", id);
							return next;
						},
						{ replace: true },
					);
				}
			} catch {
				// Error handled silently; pages will show empty state
			} finally {
				if (!cancelled) {
					setIsLoading(false);
					setIsInitialized(true);
				}
			}
		}

		fetchData();

		return () => {
			cancelled = true;
		};
	}, [user?.id]);

	return {
		companies,
		selectedCompany,
		setSelectedCompany,
		replaceCompany,
		isLoading,
		isInitialized,
	};
}
